import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from 'src/components/Layout';
import Modal from 'src/components/Modal';
import Navbar from 'src/components/NavBar';
import { AddLineItem, BlindReceivePermissions } from 'src/constants/permissionsList';
import useBoolean from 'src/hooks/useBoolean';
import usePermission from 'src/hooks/usePermissions';
import PoRelatedDocumentsContextProvider from 'src/sections/po_related_documents/PoRelatedDocumentsContext';
import ReceiptDetailsContextProvider from 'src/sections/receipt_details/ReceiptContext';
import ReceiptSplitter from 'src/sections/receipt_details/ReceiptSplitter';
import ReceiptInfo from 'src/sections/receipt_details/receiptInfo/ReceiptInfo';
import useGlCodesApi from 'src/services/useGlCodesApi';
import useReceiptDetailsApi from 'src/services/useReceiptDetailsApi';
import useReceiptItemNotesApi from 'src/services/useReceiptItemNotesApi';
import useReceiptLineItemsApi, {
  ReceiptLineItemWithSelected,
} from 'src/services/useReceiptLineItemsApi';
import { isEqualString } from 'src/utils/strings';
import ReceiptDetailsPODocuments from '../sections/po_related_documents/PoRelatedDocuments';
import BulkAssignGLWizard from '../sections/receipt_details/BulkAssignGLWizard';
import ManageInvoiceLinksWizard from '../sections/receipt_details/ManageInvoiceLinks';
import ReceiptDetailsActions from '../sections/receipt_details/ReceiptDetailsActions';
import ReceiptDetailsLineItems from '../sections/receipt_details/ReceiptDetailsLineItems';
import ReceiptNotesWizard from '../sections/receipt_details/ReceiptNotesWizard';

interface modal {
  name: string;
  open: boolean;
  width: string;
  modalComponent: React.ReactNode | null;
}

export default function ReceiptDetails() {
  const [searchParams] = useSearchParams();
  const recId = searchParams.get('recid') || '';
  const [selectedLineItemIds, setSelectedLineItemIds] = useState<number[]>([]);

  const { receiptData, receiptDataLoading, getReceiptData, isReceiptDirty, setReceiptData } =
    useReceiptDetailsApi(recId);
  const { poId } = receiptData;
  const { glCodeOptions, glCodesObject } = useGlCodesApi(
    String(receiptData.companyId),
    receiptData.glPeriodId,
    true
  );

  const { receiptItemNotes, receiptItemNotesObject, UnassignedItemNotes } =
    useReceiptItemNotesApi();

  const {
    receiptLineItems,
    setReceiptLineItems,
    isReceiptLineItemsLoading,
    getReceiptLineItems,
    isReceiptLineItemsDirty,
  } = useReceiptLineItemsApi(recId, glCodesObject, receiptItemNotesObject, UnassignedItemNotes);

  const receiptLineItemsWithSelected: ReceiptLineItemWithSelected[] = useMemo(
    () =>
      receiptLineItems.map((item) => ({
        ...item,
        selected: selectedLineItemIds.includes(item.receivingReceiptLineItemId),
      })),
    [receiptLineItems, selectedLineItemIds]
  );

  const { value: showScans, toggle: togglePreview, setBool: setShowScans } = useBoolean();

  const [modalInfo, setModalInfo] = useState<modal>({
    name: '',
    open: false,
    width: '',
    modalComponent: null,
  });

  const isBlindReceive: boolean = usePermission({ permissions: BlindReceivePermissions });
  const isAddLineItems = usePermission({ permissions: AddLineItem });
  const isInProgress = useMemo(
    () => isEqualString(receiptData?.receiptStatus, 'in progress'),
    [receiptData?.receiptStatus]
  );

  const [isCurrentDocLinked, setIsCurrentDocLinked] = useState(false);

  useEffect(() => {
    function beforeUnloadFunction(event: any) {
      event.preventDefault();
      return (event.returnValue = '');
    }
    if (isReceiptDirty || isReceiptLineItemsDirty)
      window?.addEventListener('beforeunload', beforeUnloadFunction);
    else window?.removeEventListener('beforeunload', beforeUnloadFunction);
    return () => window?.removeEventListener('beforeunload', beforeUnloadFunction);
  }, [isReceiptDirty, isReceiptLineItemsDirty]);

  const toggleModal = useCallback(
    (modalName: string, data?: any) => {
      switch (modalName) {
        case 'Receipt Notes':
          setModalInfo((prev: modal) => ({
            name: 'Receipt Notes',
            open: !prev.open,
            width: '550px',
            modalComponent: (
              <ReceiptNotesWizard
                showModal={toggleModal}
                receiptNote={receiptData.receiptNotes || ''}
              />
            ),
          }));
          break;
        case `Link a document to Receipt ${recId}`:
          setModalInfo((prev: modal) => ({
            name: `Link a document to Receipt ${recId}`,
            open: !prev.open,
            width: '65vw',
            modalComponent: <ManageInvoiceLinksWizard showModal={toggleModal} />,
          }));
          break;
        case 'Bulk Assign GL Codes':
          setModalInfo((prev: modal) => ({
            id: 'assignGLCodes',
            name: 'Bulk Assign GL Codes',
            open: !prev.open,
            width: '500px',
            modalComponent: (
              <BulkAssignGLWizard showModal={toggleModal} updateGLItems={data?.updateGLItems} />
            ),
          }));
          break;
      }
    },
    [recId, receiptData.receiptNotes]
  );

  const handleRefreshDetails = useCallback(() => {
    getReceiptData();
  }, [getReceiptData]);

  const MainContainer = useMemo(
    () => (
      <main className="flex p-1 flex-col gap-2">
        <ReceiptDetailsPODocuments
          gridKey={`${showScans}`}
          poNumber={receiptData?.poNum?.toString()}
          poId={receiptData?.poId?.toString()}
          refreshDetails={handleRefreshDetails}
          isInvoice={false}
          poStatus={receiptData?.poStatus}
          isDirty={isReceiptDirty}
          setIsCurrentDocLinked={setIsCurrentDocLinked}
          isEditDisabled={false}
        />
        <ReceiptInfo />
        <ReceiptDetailsLineItems toggleModal={toggleModal} />
      </main>
    ),
    [
      handleRefreshDetails,
      isReceiptDirty,
      receiptData?.poId,
      receiptData?.poNum,
      receiptData?.poStatus,
      showScans,
      toggleModal,
    ]
  );

  return (
    <PoRelatedDocumentsContextProvider poId={receiptData?.poId?.toString()}>
      <ReceiptDetailsContextProvider
        isInProgress={isInProgress}
        isAddLineItems={isAddLineItems}
        receiptData={receiptData}
        getReceiptData={getReceiptData}
        companyId={receiptData?.companyId?.toString()}
        isReceiptLoading={receiptDataLoading}
        setReceipt={setReceiptData}
        isReceiptDirty={isReceiptDirty}
        toggleModal={toggleModal}
        receiptLineItems={receiptLineItemsWithSelected}
        setReceiptLineItems={setReceiptLineItems}
        isReceiptLineItemsLoading={isReceiptLineItemsLoading}
        getReceiptLineItems={getReceiptLineItems}
        isReceiptLineItemsDirty={isReceiptLineItemsDirty}
        glCodeOptions={glCodeOptions}
        glCodesObject={glCodesObject}
        showScans={showScans}
        setShowScans={setShowScans}
        receiptItemNotes={receiptItemNotes}
        receiptItemNotesObject={receiptItemNotesObject}
        fullyCompletedItemNotes={UnassignedItemNotes}
        isBlindReceive={isBlindReceive}
        isCurrentDocLinked={isCurrentDocLinked}
        setSelectedLineItemIds={setSelectedLineItemIds}
        selectedLineItemIds={selectedLineItemIds}
      >
        <Navbar title="Receipt Details" />
        <Layout>
          <ReceiptDetailsActions
            receiptId={recId}
            showScans={showScans}
            TogglePreview={togglePreview}
            poId={poId}
            isCurrentDocLinked={isCurrentDocLinked}
          />
          <ReceiptSplitter main={MainContainer} />
        </Layout>
        {modalInfo.open && (
          <Modal
            width={modalInfo.width}
            title={modalInfo.name}
            visible={modalInfo.open}
            closeModal={() => toggleModal(modalInfo.name)}
          >
            {modalInfo.modalComponent}
          </Modal>
        )}
      </ReceiptDetailsContextProvider>
    </PoRelatedDocumentsContextProvider>
  );
}
